<script setup>
import { ref, computed } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import { useDraggablePanel } from './useDraggablePanel';

const props = defineProps({
  solid: { type: Boolean, default: false },
  type: {
    type: String,
    default: 'edit',
    validator: value => ['alert', 'edit'].includes(value),
  },
  title: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  cancelButtonLabel: {
    type: String,
    default: '',
  },
  confirmButtonLabel: {
    type: String,
    default: '',
  },
  disableConfirmButton: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  showCancelButton: {
    type: Boolean,
    default: true,
  },
  showConfirmButton: {
    type: Boolean,
    default: true,
  },
  closeOnClickOutside: {
    type: Boolean,
    default: true,
  },
  // Botão "X" no canto superior direito do cabeçalho.
  showCloseIcon: {
    type: Boolean,
    default: false,
  },
  overflowYAuto: {
    type: Boolean,
    default: false,
  },
  stickyHeaderFooter: {
    type: Boolean,
    default: false,
  },
  width: {
    type: String,
    default: 'lg',
    validator: value =>
      ['6xl', '5xl', '4xl', '3xl', '2xl', 'xl', 'lg', 'md', 'sm'].includes(
        value
      ),
  },
  position: {
    type: String,
    default: 'center',
    validator: value => ['center', 'top'].includes(value),
  },
  // Janela não modal, sem fundo escurecido e arrastável pelo cabeçalho: a tela
  // de trás continua visível e utilizável (ex.: ler a conversa enquanto preenche).
  floating: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['confirm', 'close']);

const { t } = useI18n();

const dialogRef = ref(null);
const dialogContentRef = ref(null);
const isOpen = ref(false);
const {
  panelStyle,
  startDrag,
  onHandleKeydown,
  reset: resetPosition,
} = useDraggablePanel(dialogRef);

const maxWidthClass = computed(() => {
  const classesMap = {
    '6xl': 'max-w-6xl',
    '5xl': 'max-w-5xl',
    '4xl': 'max-w-4xl',
    '3xl': 'max-w-3xl',
    '2xl': 'max-w-2xl',
    xl: 'max-w-xl',
    lg: 'max-w-lg',
    md: 'max-w-md',
    sm: 'max-w-sm',
  };

  return classesMap[props.width] ?? 'max-w-md';
});

const positionClass = computed(() =>
  props.position === 'top' ? 'dialog-position-top' : ''
);

const dialogOverflowClass = computed(() =>
  props.overflowYAuto
    ? 'max-h-[calc(100dvh-2rem)] overflow-hidden sm:max-h-[calc(100dvh-4rem)]'
    : 'overflow-visible'
);

const contentOverflowClass = computed(() => {
  if (props.overflowYAuto && props.stickyHeaderFooter) {
    return 'max-h-[calc(100dvh-2rem)] overflow-hidden sm:max-h-[calc(100dvh-4rem)]';
  }
  if (props.overflowYAuto) {
    return 'max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-h-[calc(100dvh-4rem)]';
  }
  return 'overflow-visible';
});

const open = () => {
  isOpen.value = true;
  if (props.floating) {
    resetPosition();
    dialogRef.value?.show();
    return;
  }
  dialogRef.value?.showModal();
};

const close = () => {
  emit('close');
  dialogRef.value?.close();
  isOpen.value = false;
};

// Only close if the close event originated from this dialog,
// not from a child dialog (e.g. ProseMirror prompt) bubbling up.
const handleDialogClose = e => e.target === dialogRef.value && close();

// Only close on click-outside if this dialog is the topmost one.
// If another dialog (e.g. ProseMirror prompt) is open on top, ignore.
const handleClickOutside = () => {
  if (!props.closeOnClickOutside || props.floating) return;

  const dialogs = document.querySelectorAll('dialog[open]');
  if (dialogs[dialogs.length - 1] === dialogRef.value) close();
};

// Floating popups (e.g. DatePicker) render on the dialog itself, outside the
// form, so they can overlay the modal. Clicks on them are not "outside".
const clickOutsideOptions = { ignore: ['[data-dialog-floating-layer]'] };

// Diálogo não modal não fecha com Esc nativamente; só reage com o foco dentro dele.
const handleFloatingEscape = event => {
  if (!props.floating || event.defaultPrevented) return;
  event.preventDefault();
  close();
};

const confirm = () => {
  emit('confirm');
};

defineExpose({ open, close });
</script>

<template>
  <TeleportWithDirection to="body">
    <dialog
      ref="dialogRef"
      class="w-full shadow-xl rounded-xl"
      :class="[
        maxWidthClass,
        positionClass,
        dialogOverflowClass,
        floating
          ? 'dialog-floating border border-n-weak shadow-2xl'
          : 'transition-all duration-300 ease-in-out',
      ]"
      :style="floating ? panelStyle : undefined"
      @close.prevent="handleDialogClose"
      @keydown.esc="handleFloatingEscape"
    >
      <OnClickOutside
        :options="clickOutsideOptions"
        @trigger="handleClickOutside"
      >
        <form
          ref="dialogContentRef"
          class="flex flex-col w-full h-auto gap-6 p-6 text-start align-middle transition-all duration-300 ease-in-out transform backdrop-blur-[100px] shadow-xl rounded-xl"
          :class="[
            contentOverflowClass,
            solid ? 'bg-n-solid-1' : 'bg-n-alpha-3',
          ]"
          @submit.prevent="confirm"
          @click.stop
        >
          <div
            v-if="title || description || $slots.header"
            class="flex flex-col gap-2"
            :class="{
              'shrink-0 -mx-6 -mt-6 border-b border-n-weak bg-n-alpha-3 px-6 py-5':
                stickyHeaderFooter,
              'relative cursor-move touch-none select-none pe-12': floating,
              'relative pe-12': showCloseIcon && !floating,
            }"
            @pointerdown="floating && startDrag($event)"
          >
            <button
              v-if="showCloseIcon && !floating"
              type="button"
              class="absolute grid size-8 place-content-center rounded-lg text-n-slate-11 transition hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
              :class="stickyHeaderFooter ? 'end-4 top-4' : 'end-0 top-0'"
              :aria-label="t('GENERAL.CLOSE')"
              :title="t('GENERAL.CLOSE')"
              @click="close"
            >
              <span class="i-lucide-x size-4" aria-hidden="true" />
            </button>
            <button
              v-if="floating"
              type="button"
              class="absolute grid size-8 cursor-move place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
              :class="stickyHeaderFooter ? 'end-4 top-4' : 'end-0 top-0'"
              :aria-label="t('DIALOG.MOVE_WINDOW')"
              :title="t('DIALOG.MOVE_WINDOW')"
              @keydown="onHandleKeydown"
            >
              <span
                class="i-hugeicons-drag-drop-vertical size-4"
                aria-hidden="true"
              />
            </button>
            <slot name="header">
              <h3 class="text-base font-medium leading-6 text-n-slate-12">
                {{ title }}
              </h3>
              <slot name="description">
                <p v-if="description" class="mb-0 text-sm text-n-slate-11">
                  {{ description }}
                </p>
              </slot>
            </slot>
          </div>
          <div
            v-if="isOpen"
            :class="{
              'min-h-0 flex-1 overflow-y-auto overscroll-contain px-1':
                stickyHeaderFooter,
            }"
          >
            <slot />
          </div>
          <!-- Dialog content will be injected here -->
          <div
            :class="
              stickyHeaderFooter
                ? 'shrink-0 -mx-6 -mb-6 border-t border-n-weak bg-n-alpha-3 px-6 py-4'
                : 'contents'
            "
          >
            <slot name="footer">
              <div
                v-if="showCancelButton || showConfirmButton"
                class="flex w-full flex-col-reverse items-stretch justify-end gap-3 sm:flex-row sm:items-center"
              >
                <Button
                  v-if="showCancelButton"
                  variant="faded"
                  color="slate"
                  :label="cancelButtonLabel || t('DIALOG.BUTTONS.CANCEL')"
                  :class="stickyHeaderFooter ? 'sm:min-w-24' : 'w-full'"
                  type="button"
                  @click="close"
                />
                <Button
                  v-if="showConfirmButton"
                  :color="type === 'edit' ? 'blue' : 'ruby'"
                  :label="confirmButtonLabel || t('DIALOG.BUTTONS.CONFIRM')"
                  :class="stickyHeaderFooter ? 'sm:min-w-24' : 'w-full'"
                  :is-loading="isLoading"
                  :disabled="disableConfirmButton || isLoading"
                  type="submit"
                />
              </div>
            </slot>
          </div>
        </form>
      </OnClickOutside>
    </dialog>
  </TeleportWithDirection>
</template>

<style scoped>
dialog::backdrop {
  @apply bg-n-alpha-black1 backdrop-blur-[4px];
}

/* Não modal: fora da camada superior, então fixa e centraliza na janela. */
.dialog-floating {
  position: fixed;
  inset: 0;
  z-index: 70;
  height: fit-content;
  margin: auto;
}

.dialog-position-top {
  margin-top: 1rem;
  margin-bottom: auto;
}

@media (min-width: 640px) {
  .dialog-position-top {
    margin-top: 2rem;
  }
}
</style>
